/* sts-auth.js — the account layer (W09 / G7-G10). NOTHING here authenticates:
   every sign-in and account creation is a native POST to Shopify.
   Notes: docs/new-theme-v4/asset-notes/sts-auth.md */
(function (w, d) {
  'use strict';

  var STS = (w.STS = w.STS || {});
  if (STS.auth) return;

  /* One place decides which backend we talk to: snippets/sts-env.liquid
     resolves the sts.api_env / sts.proxy_base_* shop metafields and publishes
     window.STS.env. This module has no section markup of its own, so it takes
     the resolved value straight from env, with the old literal as the fallback
     for a layout that does not render the head snippet. */
  function envBase(key, fallback, hard) {
    var E = STS.env;
    return String((E && E.get) ? E.get(key, fallback || hard) : (fallback || hard)).replace(/\/+$/, '');
  }

  var MODAL_ID = 'sts-auth';
  var LOGIN_PATH = '/account/login';
  var REGISTER_PATH = '/account/register';
  var NET_TIMEOUT = 2500;      // ms; every lookup below degrades rather than waits
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* The three states that mean "there is a record, but it cannot be signed into
     yet". The endpoint is deliberately a coarse existence oracle and is rate
     limited server side; nothing beyond these three panels may be surfaced from
     it (§7.6). Anything else, including every error, is treated as 'new', which
     is exactly today's behaviour. */
  var DISABLED_STATES = { activate: 1, results: 1, known: 1 };

  // ------------------------------------------------------------- utilities

  function $(sel, root) { return (root || d).querySelector(sel); }
  function $$(sel, root) { return [].slice.call((root || d).querySelectorAll(sel)); }
  function get(k) { try { return w.localStorage.getItem(k); } catch (e) { return null; } }
  function set(k, v) { try { w.localStorage.setItem(k, v); } catch (e) { /* blocked */ } }
  function parse(raw) { try { return JSON.parse(raw); } catch (e) { return null; } }
  function valid(email) { return EMAIL_RE.test(String(email || '').trim()); }

  /* Same-origin path guard for anything from a link or localStorage that is
     about to become a navigation target. SEC-08: "//x" and "/\x" (browsers
     read a backslash as a slash) leave the shop, and so can a path with a tab
     or newline in it; the URL parse is the final word. */
  function samesite(path) {
    if (typeof path !== 'string' || !/^\/(?![\/\\])/.test(path) || /[\u0000-\u001f\u007f]/.test(path)) return false;
    try { return new URL(path, w.location.origin).origin === w.location.origin; } catch (e) { return false; }
  }

  function uuid() {
    try { if (w.crypto && w.crypto.randomUUID) return w.crypto.randomUUID(); } catch (e) { /* fall through */ }
    return 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, function () { return (Math.random() * 16 | 0).toString(16); });
  }

  /* One POST helper with a hard deadline. A slow or dead endpoint must cost a
     visitor at most NET_TIMEOUT and then behave as if it had answered "no news"
     — never a spinner they cannot escape. */
  function post(url, body) {
    if (!url || !w.fetch) return Promise.reject(new Error('unavailable'));
    var ctrl = w.AbortController ? new w.AbortController() : null;
    var timer = w.setTimeout(function () { if (ctrl) ctrl.abort(); }, NET_TIMEOUT);
    return w.fetch(url, {
      method: 'POST',
      /* Explicit, because an App Proxy route identifies the customer ONLY from
         Shopify's signed `logged_in_customer_id`, which rides on the storefront
         session cookie. Same-origin is the browser default; saying so stops a
         later refactor from quietly signing every request out. */
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {}),
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (res) {
      w.clearTimeout(timer);
      return res.json().catch(function () { return {}; }).then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    }, function (err) {
      w.clearTimeout(timer);
      throw err;
    });
  }

  // ------------------------------------------------------------- analytics

  /* Spec 09 §6.9 event 4: sts_auth_submit { mode, result, routed_to }.
     Signing in and creating an account are native POSTs (see the header), so
     their result is only known on the NEXT page. A submit leaves a one-use
     marker in sessionStorage and whichever page loads next reports it:
     'success' once the visitor is signed in, 'error' when Shopify bounced the
     form back to /account/login or /account/register with its error showing.
     Results this file knows on the spot (the email already has an account, an
     activation link was sent) are reported on the spot and clear the marker.
     No email address, name or error text is ever put in a payload. */
  var PENDING = 'sts_auth_pending';
  var PENDING_TTL = 15 * 60000;

  function track(name, params) {
    try { if (typeof w.stsTrack === 'function') w.stsTrack(name, params); } catch (e) { /* never break a page */ }
  }
  function ssGet(k) { try { return w.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { w.sessionStorage.setItem(k, v); } catch (e) { /* blocked */ } }
  function ssDel(k) { try { w.sessionStorage.removeItem(k); } catch (e) { /* blocked */ } }

  function markPending(mode, surface) {
    ssSet(PENDING, JSON.stringify({ mode: mode, surface: surface, t: Date.now() }));
  }
  function authResult(mode, result, surface, routedTo) {
    track('sts_auth_submit', { mode: mode, result: result, surface: surface, routed_to: routedTo || null });
  }
  function inviteResult(surface, sent) {
    ssDel(PENDING);
    authResult('invite', sent ? 'invite_sent' : 'invite_failed', surface);
  }

  function routedTo(path) {
    var T = w.STSTrack;
    if (path.indexOf('/pages/skin-profile') > -1) return 'profile';
    if (path.indexOf('/pages/skin-type-results') > -1) return 'results';
    if (path.indexOf('/pages/customer-activate') > -1) return 'activate';
    if (T && typeof T.isQuizHref === 'function' ? T.isQuizHref(path) : path.indexOf('/pages/skin-type-quiz') > -1) return 'quiz';
    if (path === '/' || /^\/[a-z]{2}(-[a-z]{2})?\/?$/i.test(path)) return 'home';
    return 'page';
  }

  var reported = false;
  function reportPending() {
    if (reported) return;
    reported = true;
    var p = parse(ssGet(PENDING));
    if (!p) return;
    if (!p.t || Date.now() - p.t > PENDING_TTL) { ssDel(PENDING); return; }
    var path = w.location.pathname;
    var st = STS.state;
    var signedIn = !!((w.STS_TRACK && w.STS_TRACK.customerId) || (st && typeof st === 'object' && st.loggedIn));

    if (signedIn) {
      /* snippets/sts-post-auth-route may be about to move on. It leaves
         ?sts_authed=1 on the URL only when it redirects, and it never routes
         to an /account page, so the report waits for the page it settles on. */
      if (/[?&]sts_authed=1/.test(w.location.search) || path.indexOf('/account') > -1) return;
      ssDel(PENDING);
      authResult(p.mode, 'success', p.surface, routedTo(path));
      return;
    }

    var onAuthPage = path.indexOf(LOGIN_PATH) > -1 || path.indexOf(REGISTER_PATH) > -1;
    if (!onAuthPage) return;   // a /challenge stop or a slow redirect: keep waiting
    var bounced = $$('.errors, .form-error, [data-form-errors]').some(function (n) {
      return !(n.closest && n.closest('#' + MODAL_ID)) && /\S/.test(n.textContent || '');
    });
    if (!bounced) return;
    ssDel(PENDING);
    authResult(p.mode, 'error', p.surface);
  }

  // ------------------------------------------------------------ the modal

  var modal = null;         // the <dialog>
  var root = null;          // [data-sts-auth] inside it
  var cfg = {};

  function readConfig() {
    modal = d.getElementById(MODAL_ID);
    root = modal && $('[data-sts-auth]', modal);
    if (!root) return false;
    cfg = {
      check: root.getAttribute('data-sts-auth-check') || '',
      invite: root.getAttribute('data-sts-auth-invite') || '',
      returnTo: root.getAttribute('data-sts-auth-return') || '/pages/skin-profile?sts_authed=1',
      mode: root.getAttribute('data-sts-auth-mode') || 'contract'
    };
    return true;
  }

  function panels() { return $$('[data-sts-auth-panel]', root); }
  function panelNamed(name) { return $('[data-sts-auth-panel="' + name + '"]', root); }

  function status(message) {
    var box = $('[data-sts-auth-status]', root);
    if (box) box.textContent = message || '';
  }

  /* Swap. `hidden` rather than a class, so the panels that are not showing are
     out of the tab order and out of the accessibility tree — the focus trap in
     STS.ui walks visible focusables, and a hidden panel's inputs would
     otherwise be reachable by Tab from a form the visitor cannot see. */
  function show(name, opts) {
    if (!root) return;
    opts = opts || {};
    var target = panelNamed(name) || panelNamed('signin');
    panels().forEach(function (p) {
      var on = p === target;
      p.hidden = !on;
      p.classList.toggle('is-active', on);
    });
    var title = d.getElementById(MODAL_ID + '-title');
    if (title) title.textContent = target.getAttribute('data-sts-auth-name') || 'Account';
    if (!opts.silent) status('');
    if (opts.focus !== false) {
      var first = $('input:not([type="hidden"]), button', target);
      if (first) { try { first.focus(); } catch (e) {} }
    }
    return target;
  }

  /* The return target. A link may carry its own (the Skin Profile's
     create-account panel does); otherwise the modal's default. The sts_authed
     marker is what wakes snippets/sts-post-auth-route on the other side, so it
     is added to whatever we end up with. */
  function withMarker(path) {
    if (!samesite(path)) path = cfg.returnTo || '/pages/skin-profile';
    if (path.indexOf('sts_authed=1') > -1) return path;
    return path + (path.indexOf('?') > -1 ? '&' : '?') + 'sts_authed=1';
  }

  function setReturn(path) {
    var value = withMarker(path);
    $$('[data-sts-auth-returnfield]', root).forEach(function (input) { input.value = value; });
  }

  function open(name, opts) {
    if (!readConfig()) return Promise.resolve(null);
    opts = opts || {};
    setReturn(opts.returnTo);
    var panel = show(name || 'signin', { focus: false });
    if (opts.email) {
      $$('[data-sts-auth-email]', root).forEach(function (i) { if (!i.value) i.value = opts.email; });
    }
    if (STS.ui && STS.ui.open) {
      /* Focus the first real field rather than the close button the primitive
         would otherwise reach first: the visitor came here to type an email. */
      return STS.ui.open(modal, {
        returnFocus: opts.trigger || null,
        focus: panel && $('input:not([type="hidden"])', panel)
      });
    }
    /* No component layer on the page: fall back to the plain page rather than
       a dialog nobody can close. */
    w.location.href = name === 'signup' ? REGISTER_PATH : LOGIN_PATH;
    return Promise.resolve(null);
  }

  function close() {
    if (modal && STS.ui && STS.ui.close) return STS.ui.close(modal);
    return Promise.resolve(null);
  }

  // ------------------------------------------------- intent and name split

  /* One use, read and deleted by snippets/sts-post-auth-route on the next
     logged-in page. It is the backstop for the sts_authed marker: Shopify may
     interpose /challenge and drop the query string, and then this is the only
     thing that says an authentication happened. */
  function markIntent(kind) {
    set('sts_auth_intent', kind);
    if (cfg.mode === 'return_to_page') {
      var here = w.location.pathname + w.location.search;
      if (samesite(here)) set('sts_return_page', here);
    }
  }

  /* Split on the FIRST space (criterion 27): "Leslie Baumann" -> Leslie /
     Baumann, "Cher" -> Cher / "", "Ana Maria Ruiz de Santos" -> Ana / the rest.
     Guessing where a surname really begins is not possible and not worth a
     second field on a phone. */
  function splitName(form) {
    var visible = $('[data-sts-auth-name-input]', form);
    var first = $('[data-sts-auth-first]', form);
    var last = $('[data-sts-auth-last]', form);
    if (!visible || !first || !last) return;
    var value = String(visible.value || '').trim().replace(/\s+/g, ' ');
    var cut = value.indexOf(' ');
    first.value = cut < 0 ? value : value.slice(0, cut);
    last.value = cut < 0 ? '' : value.slice(cut + 1);
    /* /account/register names the visible field customer[first_name] so a
       visitor without JavaScript keeps their name; hand the name to the split. */
    if (visible.name) { first.name = visible.name; visible.removeAttribute('name'); }
  }

  // -------------------------------------------- the disabled-account branch

  /* Always resolves. Any failure — network, CORS, rate limit, malformed body —
     is 'new', which degrades to exactly the behaviour this site has today
     (§7.6). It must never throw: it sits in front of a form submit. */
  function emailCheck(email) {
    if (!valid(email) || !cfg.check) return Promise.resolve('new');
    return post(cfg.check, { email: String(email).trim() }).then(function (r) {
      var state = r && r.data && r.data.state;
      return typeof state === 'string' ? state.toLowerCase() : 'new';
    }, function () { return 'new'; });
  }

  /* The activation invite. This is the LEGACY, UNAUTHENTICATED endpoint on
     shop.metafields.custom.customer_activate_url (spec risk 6, backend item
     G8b): it accepts {email, sendInvite} from anyone. It is called here only on
     a deliberate visitor action — submitting the create form, or pressing "email
     me a link" — never on a keystroke and never on a blur, so this file does not
     turn it into a mail cannon. Hardening it is tracked as G8b; until that
     lands, the restraint is the mitigation.

     Resolves true/false rather than rejecting: the caller shows the same "we
     found your account" panel either way, because telling a visitor "we could
     not email you" when the record may well have been invited is worse than
     telling them to check their inbox and, failing that, use "forgot password". */
  function sendInvite(email) {
    if (!cfg.invite || !valid(email)) return Promise.resolve(false);
    return post(cfg.invite, { email: String(email).trim(), sendInvite: true })
      .then(function (r) { return !!(r && r.ok); }, function () { return false; });
  }

  /* `sent` is the invite endpoint's answer. Claiming "check your inbox" for an
     email that was never sent is the one outcome worse than the dead end this
     panel exists to replace, so a failure says so and offers the retry. */
  function showFound(email, sent) {
    var panel = show('found');
    if (!panel) return;
    var body = $('[data-sts-auth-found-body]', panel);
    var retry = $('[data-sts-auth-found-retry]', panel);
    if (body) {
      if (body.__tpl == null) body.__tpl = body.textContent;
      body.textContent = sent
        ? body.__tpl.replace(/\{email\}/g, email)
        : 'We found an account for ' + email + ', but we could not send the link just now.';
    }
    if (retry) {
      retry.hidden = !!sent;
      retry.__email = email;
    }
  }

  function hint(form, message, withButton) {
    var box = $('[data-sts-auth-hint]', form);
    if (!box) return;
    var text = $('.sts-auth__hint-text', box);
    if (text) text.textContent = message || '';
    var btn = $('[data-sts-auth-invite-btn]', box);
    if (btn) btn.hidden = !withButton;
    box.hidden = !message;
  }

  // ---------------------------------------------------------- modal wiring

  function wireModal() {
    if (!readConfig() || root.__sts) return;
    root.__sts = 1;

    /* Panel switches, the "email me a link" button and the close button all go
       through one delegated listener — the panels are re-shown many times and
       per-element listeners would stack up. */
    root.addEventListener('click', function (ev) {
      var go = ev.target.closest && ev.target.closest('[data-sts-auth-go]');
      if (go) {
        ev.preventDefault();
        show(go.getAttribute('data-sts-auth-go'));
        return;
      }
      var invite = ev.target.closest &&
        ev.target.closest('[data-sts-auth-invite-btn],[data-sts-auth-found-retry]');
      if (invite) {
        ev.preventDefault();
        var form = invite.closest('form');
        var email = form && $('[data-sts-auth-email]', form);
        var address = invite.__email || (email ? email.value.trim() : '');
        if (!valid(address)) return;
        invite.setAttribute('aria-busy', 'true');
        sendInvite(address).then(function (sent) {
          invite.removeAttribute('aria-busy');
          inviteResult('modal', sent);
          showFound(address, sent);
        });
      }
    });

    var signin = $('#sts-auth-signin', root);
    var signup = $('#sts-auth-signup', root);

    if (signin) {
      /* Sign in stays a plain native POST. The only addition is the one-use
         intent marker and, if the lookup below found a record that was never
         activated, a hint — shown, never acted on, because a visitor who
         remembers their password should not be interrupted. */
      signin.addEventListener('submit', function () { markIntent('signin'); markPending('signin', 'modal'); });
      watchEmail(signin, function (state) {
        if (DISABLED_STATES[state]) {
          hint(signin, 'This email has an account that has not been set up yet.', true);
        } else {
          hint(signin, '', false);
        }
      });
    }

    if (signup) {
      watchEmail(signup, function (state) {
        if (state === 'account') {
          hint(signup, 'This email already has an account. Sign in to see your routine.', false);
        } else if (DISABLED_STATES[state]) {
          hint(signup, 'We already have this email on file. Creating the account will email you a link instead.', false);
        } else {
          hint(signup, '', false);
        }
      });

      signup.addEventListener('submit', function (ev) {
        splitName(signup);
        markIntent('signup');
        markPending('signup', 'modal');

        /* Second pass — the check already ran and said "new". Let Shopify have
           it. */
        if (signup.__stsGo) return;

        var email = $('[data-sts-auth-email]', signup);
        var address = email ? email.value.trim() : '';
        if (!valid(address) || !cfg.check) return;   // Shopify's own validation

        /* The one place the native POST is held: criterion 25. Without it, the
           single most common visitor on this site — someone who took the quiz,
           gave an email, and now wants an account — is told "email has already
           been taken" and has nowhere to go. */
        ev.preventDefault();
        var submit = $('button[type="submit"]', signup);
        if (submit) submit.setAttribute('aria-busy', 'true');
        status('Checking your email…');

        emailCheck(address).then(function (state) {
          if (submit) submit.removeAttribute('aria-busy');
          status('');
          if (state === 'account') {
            ssDel(PENDING);
            authResult('signup', 'existing_account', 'modal');
            show('signin');
            var field = $('#sts-auth-signin-email');
            if (field) field.value = address;
            hint($('#sts-auth-signin', root), 'This email already has an account. Sign in to see your routine.', false);
            return;
          }
          if (DISABLED_STATES[state]) {
            sendInvite(address).then(function (sent) { inviteResult('modal', sent); showFound(address, sent); });
            return;
          }
          signup.__stsGo = 1;
          signup.submit();
        });
      });
    }
  }

  /* Debounced blur/idle lookup, shared by both panels and by the account pages.
     Debounced on input and run again on blur, so a visitor who tabs straight on
     still gets the answer; the endpoint is rate limited at 12/min per IP, which
     this stays well inside. */
  function watchEmail(form, render) {
    var field = $('[data-sts-auth-email]', form);
    if (!field || field.__stsWatch) return;
    field.__stsWatch = 1;
    var timer = null;
    var last = '';

    function run() {
      var address = field.value.trim();
      if (address === last) return;
      last = address;
      if (!valid(address)) { render('new', address); return; }
      emailCheck(address).then(function (state) { render(state, address); });
    }
    field.addEventListener('input', function () {
      w.clearTimeout(timer);
      timer = w.setTimeout(run, 700);
    });
    field.addEventListener('blur', function () {
      w.clearTimeout(timer);
      run();
    });
  }

  // --------------------------------------------------- link interception

  /* Every /account/login and /account/register link on the site opens the modal
     instead. Delegated at the document, so markup rendered later — a drawer, a
     re-rendered section in the theme editor — is covered without re-wiring.

     indexOf, not a prefix match: a localised storefront renders the route as
     /en-ca/account/login. Modified clicks (new tab, download, middle button)
     and anything marked data-sts-no-modal are left alone. */
  function interceptLinks() {
    d.addEventListener('click', function (ev) {
      if (ev.defaultPrevented || ev.button || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      var a = ev.target.closest && ev.target.closest('a[href]');
      if (!a || a.hasAttribute('data-sts-no-modal') || a.target === '_blank') return;
      var href = a.getAttribute('href') || '';
      var isLogin = href.indexOf(LOGIN_PATH) > -1;
      var isRegister = href.indexOf(REGISTER_PATH) > -1;
      /* The bare account route: the header's account icon links /account, which
         Shopify answers for a signed-out visitor with /account/login?return_url=
         /account. The v6 prototype's account button (accountAction) opens Sign
         in instead, so this does too, and returns to /account as Shopify would. */
      var path = href.split(/[?#]/)[0].replace(/^https?:\/\/[^\/]+/i, '');
      var isAccount = /^(\/[a-z]{2}(-[a-z]{2})?)?\/account\/?$/i.test(path);
      if (!isLogin && !isRegister && !isAccount) return;
      if (!d.getElementById(MODAL_ID)) return;     // logged in: no modal rendered

      /* A link may name its own return target — the Skin Profile's
         create-account panel does (return_to), the quiz's sign-in links use
         Shopify's return_url. Keep either. */
      var returnTo = isAccount ? path : null;
      var q = href.indexOf('?');
      if (q > -1) {
        try {
          var sp = new w.URLSearchParams(href.slice(q + 1));
          returnTo = sp.get('return_to') || sp.get('return_url') || returnTo;
        } catch (e) { /* keep what we have */ }
      }

      ev.preventDefault();
      open(isRegister ? 'signup' : 'signin', { trigger: a, returnTo: returnTo });
    });
  }

  // ------------------------------------------- /account/login and /register

  /* The pages behind the modal. Three jobs, none of which needs the section
     files to change:

       - flag the wrapper so assets/sts-auth.css can restyle it;
       - carry ?return_to= into the native forms, so a wrong password comes back
         to a styled retry that still lands in the right place (criterion 24);
       - run the disabled-account branch here too, including the specific case
         of Shopify having just answered "email has already been taken".        */
  /* True for anything that is NOT part of the modal. The modal is rendered on
     /account/login and /account/register as well, with the same form actions and
     the same Shopify error markup, so every page-level query has to say which of
     the two it means. */
  function outside(node) {
    return !(node.closest && node.closest('#' + MODAL_ID));
  }

  var enhanced = false;
  function enhanceAccountPages() {
    if (enhanced) return;
    var path = w.location.pathname;
    var onLogin = path.indexOf(LOGIN_PATH) > -1;
    var onRegister = path.indexOf(REGISTER_PATH) > -1;
    if (!onLogin && !onRegister) return;
    enhanced = true;

    $$('.full__page__form__wrapper').forEach(function (n) { n.classList.add('sts-auth-page'); });

    /* Only the two forms that authenticate: the sign-in form and the one with a
       password field (create account). Password recovery posts to
       /account/recover and has no business carrying a return target.

       The modal is on this page too — it is rendered for every signed-out
       visitor — and its three forms have the same actions. `outside` keeps this
       to the page's own forms; wireModal has already dealt with the modal's,
       and touching them twice would double every listener. */
    var authForms = $$('form[action*="/account"]').filter(outside).filter(function (form) {
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/account/recover') > -1) return false;
      return action.indexOf('/account/login') > -1 ||
        !!form.querySelector('input[name="customer[password]"]');
    });

    var returnTo = null;
    try { returnTo = new w.URLSearchParams(w.location.search).get('return_to'); } catch (e) {}

    authForms.forEach(function (form) {
      var isLogin = (form.getAttribute('action') || '').indexOf('/account/login') > -1;
      if (samesite(returnTo) && !form.querySelector('input[name="return_to"]')) {
        var input = d.createElement('input');
        input.type = 'hidden';
        input.name = 'return_to';
        input.value = withMarker(returnTo);
        form.appendChild(input);
      }
      /* Mark the intent here too, so the router fires for a visitor who never
         saw the modal — JavaScript-off first load, a bookmarked /account/login,
         or a bounce back from a form error. */
      form.addEventListener('submit', function () {
        if (!isLogin) splitName(form);
        markIntent(isLogin ? 'signin' : 'signup');
        markPending(isLogin ? 'signin' : 'signup', 'page');
      });
    });

    if (!readConfig()) return;

    /* The two dead ends this whole branch exists to remove (§7.6, criterion
       25). Both are what a DISABLED customer record looks like from the outside:
       "email has already been taken" when they try to create the account, and
       "incorrect email or password" when they try to sign in to one that has no
       password. Shopify has already answered; re-check and offer the invite in
       place of the error. The error is only replaced when the lookup confirms
       the record is un-activated — a genuinely wrong password still says so. */
    var errors = $$('.errors, .form-error, [data-form-errors]')
      .filter(outside)
      .filter(function (n) {
        return /already been taken|incorrect email or password/i.test(n.textContent || '');
      });
    if (!errors.length) return;
    var emailField = $('#Email') || $('#CustomerEmail') ||
      $$('input[name="customer[email]"]').filter(outside)[0];
    var address = emailField ? emailField.value.trim() : '';
    if (!valid(address)) return;

    emailCheck(address).then(function (state) {
      if (!DISABLED_STATES[state]) return;
      var slot = $('#stsErrorMessage') || errors[0];
      var box = d.createElement('div');
      box.className = 'sts-auth-page__found';
      box.innerHTML = '<p class="sts-auth-page__found-title">We found your account</p>' +
        '<p class="sts-auth-page__found-body"></p>' +
        '<button type="button" class="sts-btn sts-btn--primary sts-btn--md">Email me a link to set a password</button>';
      $('.sts-auth-page__found-body', box).textContent =
        'This email already has an account that was never set up. We can email you a link so you can choose a password.';
      var btn = $('button', box);
      btn.addEventListener('click', function () {
        btn.setAttribute('aria-busy', 'true');
        sendInvite(address).then(function (sent) {
          btn.removeAttribute('aria-busy');
          inviteResult('page', sent);
          $('.sts-auth-page__found-body', box).textContent =
            "We've emailed a link to " + address + ' so you can set a password.';
          btn.hidden = true;
        });
      });
      slot.parentNode.insertBefore(box, slot.nextSibling);
      errors.forEach(function (n) { n.hidden = true; });
    });
  }

  // ------------------------------------------------ guest-result attach (G10)

  /* WHICH result is written to the account, and what the panel is allowed to
     call it. `slqSavedResults` is written by the quiz only when an email was
     captured; `customerSkinType` is rewritten by EVERY completion, in any tab.
     So a re-quiz that skipped the email leaves an OLDER type's token in the
     saved blob, and snippets/sts-profile-attach.liquid offered to save the type
     the device currently holds while this call attached the other one — to the
     account metafield, permanently, on the one surface here that writes to the
     server. One question, one answer: PR #216's rule, applied here.

     Ordered, and every source is tried, taking the first whose OWN declared
     type agrees with the device's. `slqResultsHandoff` leads because it is this
     tab's just-finished quiz, but it carries no timestamp (see the note at
     assets/sts-liquid-quiz.js), so the order alone cannot be trusted — the
     agreement test is what lets a stale same-tab handoff lose to a saved blob
     that matches. Where nothing agrees, or the device names no type, the first
     token wins exactly as it always has; wireAttach() then makes the panel name
     THAT result rather than a type this call will not send. */
  function attachSource() {
    var want = String(get('customerSkinType') || '').toUpperCase();
    var raw = [null, parse(get('slqSavedResults'))];
    try { raw[0] = parse(w.sessionStorage.getItem('slqResultsHandoff')); } catch (e) { /* blocked */ }
    var first = null;
    for (var i = 0; i < raw.length; i++) {
      var src = raw[i];
      if (!src || !src.widgetToken) continue;
      var hit = { token: String(src.widgetToken), type: String(src.skinType || '').toUpperCase() };
      if (!first) first = hit;
      if (want && hit.type === want) return hit;
    }
    return first;
  }

  /* §7.3, criterion 30. A visitor takes the quiz logged out, then signs in to
     an account that has no skin type. The type and routine are on the device;
     the account should end up with them.
     *
     * POST /apps/quiz/attach-result is built in skin-type-pro but is only on
     * its DEV environment; the storefront proxy points at production, so today
     * it answers 401. This is the documented seam: the call is written, and
     * every failure path — 401 because the route is not live or nobody is
     * signed in, 404, a network error, blocked storage — changes NOTHING on the
     * device. The guest result stays exactly where it is, so when the route
     * does ship the same visitor's next attempt works. Nothing here ever
     * deletes slqSavedResults.
     *
     * 409 ALREADY_TYPED counts as success and is the idempotency contract: a
     * second call with the same token means the first one landed.
     *
     * Resolves one of three values, because 401 is not the same answer as a
     * failure and must not be shown as one. They stay boolean-safe, so a caller
     * that only asks `if (ok)` still behaves as it did:
     *     true    the account now has the result — reload
     *     null    401: not signed in, or the route is not live. The result is
     *             still on the device; ask for the sign-in that would make the
     *             call work rather than printing an error
     *     false   anything else
     *
     * WS05 can use this without knowing any of that: put
     * `data-sts-attach-result` on the "Save this result to my account" button
     * and it is wired below, or call STS.auth.attachResult() directly.         */
  function attachResult() {
    var src = attachSource();
    var token = src && src.token;
    if (!token) return Promise.resolve(false);
    /* nonce=<uuid>: sign-in lands with the page's own proxy reads in the same
       second, and one signature read-then-written is a 401 replay (#1091). */
    return post(envBase('proxyBase', '', '/apps/quiz') + '/attach-result?nonce=' + uuid(), { widgetToken: token }).then(function (r) {
      if (r.ok) return true;
      if (r.status === 409) return true;           // already attached
      if (r.status === 401) return null;           // not signed in / not live
      return false;
    }, function () { return false; });
  }

  function wireAttach() {
    $$('[data-sts-attach-result]').forEach(function (btn) {
      /* Name the result THIS button will send. The panel used to print the
         device's current type, which is a second answer to the question
         attachSource() has already answered — "save your ORNW routine" over a
         button that attaches DSNT. Done here rather than in the snippet so the
         two can never disagree, and after boot so STS.auth is certainly loaded:
         sts-state.js raises the pendingAttach flag from a deferred script that
         runs BEFORE this one. The confirmation panel is not touched; its type
         is printed from the metafield in Liquid, which after a successful
         attach is the only authority. */
      var panel = btn.closest('[data-sts-attach-panel]');
      if (panel) {
        var src = attachSource();
        if (src && src.type) {
          $$('[data-sts-attach-type]', panel).forEach(function (slot) { slot.textContent = src.type; });
        }
      }
      if (btn.__sts) return;
      btn.__sts = 1;
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        btn.setAttribute('aria-busy', 'true');
        attachResult().then(function (result) {
          btn.removeAttribute('aria-busy');
          if (result === null) {
            /* The visitor is holding a result and is not signed in — which is
               the whole reason this button exists. Open the panel that fixes
               it instead of telling them something went wrong. */
            open('signin', { trigger: btn, returnTo: w.location.pathname });
            return;
          }
          if (result !== true) {
            /* A popup, not a toast (v5.1 §1.5): the customer must notice it. */
            if (STS.popups && STS.popups.notice) {
              STS.popups.notice({ title: 'We could not save that result just now', body: 'Nothing was lost. Try again in a moment.' });
            }
            return;
          }
          /* Reload rather than refresh in place: the type now comes from the
             metafield, and everything server-rendered (the header badge, the
             profile, the palette) is resolved in Liquid. */
          w.location.reload();
        });
      });
    });
  }

  // ------------------------------------------------------------------- boot

  function boot() {
    reportPending();
    wireModal();
    enhanceAccountPages();
    wireAttach();
  }

  STS.auth = {
    open: open,
    close: close,
    show: show,
    emailCheck: emailCheck,
    sendInvite: sendInvite,
    attachResult: attachResult,
    refresh: boot
  };

  interceptLinks();
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();
  d.addEventListener('shopify:section:load', boot);
}(window, document));
